import React, {
  useRef,
  useState,
  memo,
  useCallback,
} from "react";
import { Event } from "../../types/events";
import {
  SchedulerTheme,
  TimeSlot,
  ViewConfig,
  ViewInteractions,
} from "../../types/views";
import GridRow from "./GridRow";
import { usePopper } from "react-popper";
import { TimelineResourceRow } from "./timelineRows";
import { TIMELINE_RESOURCE_ROW_HEIGHT } from "./timelineConstants";
import { ResourceLaneAssignment } from "./timelineLanes";
import TimelineEventItem from "./TimelineEventItem";

interface GridContainerProps {
  events: Event[];
  resourceRows: TimelineResourceRow[];
  resourceRowMap: Map<string, TimelineResourceRow>;
  resourceLaneMap: Map<string, ResourceLaneAssignment>;
  totalHeight: number;
  getEventResourceIds?: (event: Event) => (string | number)[];
  startDate: Date;
  daysToShow: number;
  zoom: number;
  baseDayWidth: number;
  onTimeSlotClick?: ViewInteractions["onTimeSlotClick"];
  onEventClick?: ViewInteractions["onEventClick"];
  showTooltip?: boolean;
  viewConfig?: ViewConfig;
  isDraggable?: boolean;
  schedulerTheme?: SchedulerTheme;
}

const GridContainer: React.FC<GridContainerProps> = memo(
  ({
    events,
    resourceRows,
    resourceRowMap,
    resourceLaneMap,
    totalHeight,
    getEventResourceIds = (event) => event.assignedResources || [],
    startDate,
    daysToShow,
    zoom,
    baseDayWidth,
    onTimeSlotClick,
    onEventClick,
    showTooltip = true,
    viewConfig,
    isDraggable = true,
    schedulerTheme,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [hoveredSlot, setHoveredSlot] = useState<TimeSlot | null>(null);
    const [referenceElement, setReferenceElement] =
      useState<HTMLDivElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
      null
    );

    const { styles, attributes: popperAttributes } = usePopper(
      referenceElement,
      popperElement,
      {
        placement: "top-start",
        modifiers: [
          { name: "offset", options: { offset: [0, -4] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ],
      }
    );

    const internalOnCellClick = useCallback(
      (slot: TimeSlot) => {
        onTimeSlotClick?.(slot, null as any);
      },
      [onTimeSlotClick]
    );

    const internalOnCellHover = useCallback(
      (slot: TimeSlot | null, eventTarget?: React.MouseEvent) => {
        if (!showTooltip) {
          return;
        }

        setHoveredSlot(slot);
        if (slot && eventTarget) {
          setReferenceElement(eventTarget.currentTarget as HTMLDivElement);
        } else {
          setReferenceElement(null);
        }
      },
      [showTooltip]
    );

    return (
      <div
        ref={containerRef}
        className="grid-container"
        style={{
          flex: 1,
          overflow: "visible",
          position: "relative",
          backgroundColor: "white",
        }}
      >
        <div
          className="grid-content"
          style={{
            width: `${daysToShow * baseDayWidth * zoom}px`,
            position: "relative",
            height: `${Math.max(totalHeight, TIMELINE_RESOURCE_ROW_HEIGHT)}px`,
            minHeight: "100%",
          }}
        >
          {resourceRows.map((row) => (
            <GridRow
              key={row.id}
              resource={row.resource}
              startDate={startDate}
              daysToShow={daysToShow}
              cellWidth={(baseDayWidth * zoom) / (24 * 2)}
              cellHeight={row.height}
              top={row.top}
              onCellClick={internalOnCellClick}
              onCellHover={internalOnCellHover}
              viewConfig={viewConfig}
              isDraggable={isDraggable}
              schedulerTheme={schedulerTheme}
            />
          ))}

          {events.flatMap((eventItem) => {
            const resourceIds = getEventResourceIds(eventItem);

            if (!resourceIds?.length) {
              return [];
            }

            return resourceIds
              .map((resourceId) => {
                if (!resourceRowMap.has(String(resourceId))) {
                  return null;
                }

                const laneInfo = resourceLaneMap.get(String(resourceId));
                const laneIndex =
                  laneInfo?.assignments.get(String(eventItem.id)) ?? 0;
                const laneCount = laneInfo?.laneCount ?? 1;

                return (
                  <TimelineEventItem
                    key={`${eventItem.id}-${resourceId}`}
                    eventItem={eventItem}
                    resourceId={resourceId}
                    resourceRowMap={resourceRowMap}
                    startDate={startDate}
                    daysToShow={daysToShow}
                    zoom={zoom}
                    baseDayWidth={baseDayWidth}
                    laneIndex={laneIndex}
                    laneCount={laneCount}
                    onEventClick={onEventClick}
                    viewConfig={viewConfig}
                    isDraggable={isDraggable}
                    schedulerTheme={schedulerTheme}
                  />
                );
              })
              .filter(Boolean);
          })}
        </div>
        {showTooltip && hoveredSlot && (
          <div
            ref={setPopperElement}
            style={{
              ...styles.popper,
              zIndex: 200,
              backgroundColor: "white",
              border: "1px solid #ccc",
              padding: "5px",
              borderRadius: "3px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            }}
            {...popperAttributes.popper}
          >
            <div>
              {hoveredSlot.start.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              -{" "}
              {hoveredSlot.end.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
            <div>Resource: {hoveredSlot.resources[0]?.name || "N/A"}</div>
            {hoveredSlot.events.length > 0 && (
              <div>
                {hoveredSlot.events.length} event
                {hoveredSlot.events.length > 1 ? "s" : ""}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

GridContainer.displayName = "GridContainer";

export default GridContainer;
